import { PAGE_BASE_CSS } from "./page-style.ts";

/**
 * One shared "browse what was exported" table page, used for every module's
 * top-level index.html (Teams, Mail, Calendar, Contacts, Tasks, OneNote,
 * OneDrive) and for Teams' per-chat global index specifically.
 *
 * This is the narrower, incremental version of ARCHITECTURE.md §15 step 9
 * ("Viewer — offline HTML over the export"): one index per module rather
 * than a single unified cross-module viewer, built from whatever the module
 * already wrote to disk — no extra Graph calls, no build step, safe to
 * re-run. A pure string template (no DOM) so it stays unit-testable under
 * plain Node, the same split every module's render.ts already uses.
 *
 * Search/sort/filter is plain inlined vanilla JS, deliberately dependency-
 * free — this file is opened directly from disk, potentially offline, months
 * after the export ran.
 */

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** `<`/`>` closes a script tag early if left unescaped inside embedded JSON. */
function escapeForInlineScript(json: string): string {
    return json.replace(/</g, "\\u003c").replace(/>/g, "\\u003e");
}

/** Plain (non-i18n) byte formatting for exported HTML — see page-style.ts. */
export function formatBytesPlain(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 1024) {
        return `${Math.max(0, Math.round(bytes))} B`;
    }
    const units = ["KB", "MB", "GB", "TB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
}

export interface IndexColumn {
    key: string;
    label: string;
    /** Sorted and right-aligned as a number rather than as text. */
    numeric?: boolean;
}

export interface IndexRow {
    /** Display text per column key, already plain text (this module escapes it). */
    cells: Record<string, string>;
    /**
     * Raw values to sort/search by, when they differ from the display text —
     * e.g. a "Date" column displays "14 Mar 2024" but sorts by an ISO string
     * or timestamp. Defaults to `cells`.
     */
    sortValues?: Record<string, string | number>;
    /** When present, the first column's text becomes a link to this href. */
    href?: string;
}

export interface RelatedLink {
    label: string;
    href: string;
}

export interface IndexPageOptions {
    title: string;
    subtitle: string;
    columns: readonly IndexColumn[];
    rows: readonly IndexRow[];
    emptyMessage: string;
    searchPlaceholder: string;
    /** Links to related whole-module files (e.g. contacts.vcf), shown above the table. */
    relatedLinks?: readonly RelatedLink[];
}

const TABLE_CSS = `
    .bar { display: flex; gap: 0.6rem; align-items: center; margin: 1rem 0; flex-wrap: wrap; }
    .bar input[type=text] { flex: 1 1 260px; min-width: 200px; padding: 0.45rem 0.7rem; border: 1px solid #c8c6c4; border-radius: 4px; font: inherit; }
    .related { margin-bottom: 0.5rem; font-size: 0.85rem; }
    .related a { color: #0067b8; margin-right: 0.9rem; }
    .count { color: #5b6570; font-size: 0.82rem; margin-bottom: 0.5rem; }
    table { width: 100%; border-collapse: collapse; }
    thead th { position: sticky; top: 0; background: #6264a7; color: #fff; text-align: left; padding: 0.5rem 0.7rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; user-select: none; white-space: nowrap; }
    thead th .arrow { opacity: 0.6; margin-left: 0.25rem; }
    tbody td { padding: 0.45rem 0.7rem; border-bottom: 1px solid #e1dfdd; vertical-align: top; font-size: 0.88rem; }
    tbody tr:hover { background: #f5f5fb; }
    td a { color: #1b1f24; text-decoration: none; font-weight: 600; }
    td a:hover { text-decoration: underline; }
    .empty { padding: 2rem 0; text-align: center; color: #9aa4b0; }
    mark { background: #fff3a3; padding: 0; }
    @media (prefers-color-scheme: dark) {
        .bar input[type=text] { background: #1f2429; border-color: #333940; color: #e6e9ed; }
        thead th { background: #3b3d6e; }
        tbody td { border-color: #2a2f36; }
        tbody tr:hover { background: #1f2429; }
        td a { color: #e6e9ed; }
        .related a { color: #6ea8e0; }
    }
`;

function inlineScript(columns: readonly IndexColumn[]): string {
    // Deliberately verbose/readable over minified — this ships inside a
    // human-inspectable export artifact, not the app bundle.
    return `
(function () {
    var cols = ${JSON.stringify(columns.map((c) => c.key))};
    var numericCols = ${JSON.stringify(columns.filter((c) => c.numeric).map((c) => c.key))};
    var sortKey = cols[0];
    var sortDir = 1;

    function esc(s) {
        return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function highlight(text, terms) {
        var str = String(text);
        var lower = str.toLowerCase();
        var ranges = [];
        for (var i = 0; i < terms.length; i++) {
            var term = terms[i];
            if (!term) continue;
            var from = 0;
            while (true) {
                var pos = lower.indexOf(term, from);
                if (pos < 0) break;
                ranges.push([pos, pos + term.length]);
                from = pos + term.length;
            }
        }
        if (ranges.length === 0) return esc(str);
        ranges.sort(function (a, b) { return a[0] - b[0]; });
        var merged = [ranges[0]];
        for (var r = 1; r < ranges.length; r++) {
            var last = merged[merged.length - 1];
            if (ranges[r][0] <= last[1]) { last[1] = Math.max(last[1], ranges[r][1]); }
            else { merged.push(ranges[r]); }
        }
        var out = '';
        var cursor = 0;
        for (var m = 0; m < merged.length; m++) {
            out += esc(str.slice(cursor, merged[m][0]));
            out += '<mark>' + esc(str.slice(merged[m][0], merged[m][1])) + '</mark>';
            cursor = merged[m][1];
        }
        out += esc(str.slice(cursor));
        return out;
    }

    function currentRows() {
        var q = document.getElementById('q').value.toLowerCase().trim();
        var terms = q ? q.split(/\\s+/) : [];
        if (terms.length === 0) return DATA.slice();
        return DATA.filter(function (row) {
            var hay = cols.map(function (k) { return String(row.cells[k] || ''); }).join(' ').toLowerCase();
            for (var i = 0; i < terms.length; i++) {
                if (hay.indexOf(terms[i]) < 0) return false;
            }
            return true;
        });
    }

    function render() {
        var rows = currentRows();
        var q = document.getElementById('q').value.toLowerCase().trim();
        var terms = q ? q.split(/\\s+/) : [];
        var numeric = numericCols.indexOf(sortKey) >= 0;

        rows.sort(function (a, b) {
            var x = (a.sortValues && (sortKey in a.sortValues)) ? a.sortValues[sortKey] : a.cells[sortKey];
            var y = (b.sortValues && (sortKey in b.sortValues)) ? b.sortValues[sortKey] : b.cells[sortKey];
            if (numeric) { return ((Number(x) || 0) - (Number(y) || 0)) * sortDir; }
            x = String(x == null ? '' : x).toLowerCase();
            y = String(y == null ? '' : y).toLowerCase();
            if (x < y) return -1 * sortDir;
            if (x > y) return 1 * sortDir;
            return 0;
        });

        var html = '';
        for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            html += '<tr>';
            for (var c = 0; c < cols.length; c++) {
                var key = cols[c];
                var text = highlight(row.cells[key] || '', terms);
                if (c === 0 && row.href) {
                    html += '<td><a href="' + esc(row.href) + '">' + text + '</a></td>';
                } else {
                    html += '<td>' + text + '</td>';
                }
            }
            html += '</tr>';
        }

        var tbody = document.getElementById('tbody');
        tbody.innerHTML = html || ('<tr><td colspan="' + cols.length + '" class="empty">No match.</td></tr>');
        document.getElementById('count').textContent = rows.length.toLocaleString() + ' of ' + DATA.length.toLocaleString() + ' shown';

        var ths = document.querySelectorAll('thead th');
        for (var j = 0; j < ths.length; j++) {
            var arrow = ths[j].querySelector('.arrow');
            if (!arrow) continue;
            arrow.textContent = (ths[j].getAttribute('data-key') === sortKey) ? (sortDir === 1 ? '\\u25B2' : '\\u25BC') : '';
        }
    }

    document.addEventListener('DOMContentLoaded', function () {
        var ths = document.querySelectorAll('thead th');
        for (var i = 0; i < ths.length; i++) {
            (function (th) {
                th.addEventListener('click', function () {
                    var key = th.getAttribute('data-key');
                    if (sortKey === key) { sortDir = -sortDir; } else { sortKey = key; sortDir = 1; }
                    render();
                });
            })(ths[i]);
        }
        document.getElementById('q').addEventListener('input', render);
        render();
    });
})();`;
}

export function renderIndexPage(options: IndexPageOptions): string {
    const { title, subtitle, columns, rows, emptyMessage, searchPlaceholder, relatedLinks } = options;

    const headerCells = columns
        .map((c) => `<th data-key="${escapeHtml(c.key)}">${escapeHtml(c.label)}<span class="arrow"></span></th>`)
        .join("");

    const relatedHtml =
        relatedLinks && relatedLinks.length > 0
            ? `<div class="related">${relatedLinks
                  .map((l) => `<a href="${escapeHtml(l.href)}">${escapeHtml(l.label)}</a>`)
                  .join("")}</div>`
            : "";

    const data = rows.map((r) => ({
        cells: r.cells,
        ...(r.sortValues ? { sortValues: r.sortValues } : {}),
        ...(r.href ? { href: r.href } : {})
    }));

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(title)}</title>
<style>
    ${PAGE_BASE_CSS}
    body { max-width: 64rem; }
    ${TABLE_CSS}
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
<p class="meta">${escapeHtml(subtitle)}</p>
${relatedHtml}
${
    rows.length === 0
        ? `<p class="empty">${escapeHtml(emptyMessage)}</p>`
        : `<div class="bar"><input type="text" id="q" placeholder="${escapeHtml(
              searchPlaceholder
          )}" autocomplete="off" /></div>
<div class="count" id="count"></div>
<table><thead><tr>${headerCells}</tr></thead><tbody id="tbody"></tbody></table>
<script>var DATA = ${escapeForInlineScript(JSON.stringify(data))};</script>
<script>${inlineScript(columns)}</script>`
}
</body>
</html>
`;
}
